import React from 'react';

export const surface = {
  section: 'bg-surface-1',
  card: 'bg-surface-2',
  tile: 'bg-white/[0.03]',
  hover: 'hover:bg-white/[0.05]',
};

export const Eyebrow = ({ children, className = '' }) => (
  <p className={`text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500 ${className}`}>
    {children}
  </p>
);

export const Chip = ({ active = false, icon, children, className = '', ...props }) => (
  <button
    type="button"
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
      active
        ? 'bg-white/[0.1] text-white'
        : 'bg-white/[0.04] text-muted-300 hover:bg-white/[0.08] hover:text-white'
    } ${className}`}
    {...props}
  >
    {icon && <span className="material-icons text-sm">{icon}</span>}
    {children}
  </button>
);

export const LegendDot = ({ color, children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-muted-300">
    <span className={`h-2 w-2 rounded-full ${color}`} aria-hidden />
    {children}
  </span>
);

export const SegmentedControl = ({ options, value, onChange, className = '' }) => (
  <div className={`inline-flex max-w-full overflow-x-auto rounded-xl ${surface.card} p-1 ${className}`}>
    {options.map((option) => {
      const active = value === option.id;
      return (
        <button
          key={option.id}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(option.id)}
          className={`inline-flex shrink-0 items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
            active ? 'bg-white/[0.08] text-white' : 'text-muted-400 hover:text-white'
          }`}
        >
          {option.icon && <span className="material-icons text-sm">{option.icon}</span>}
          {option.label}
        </button>
      );
    })}
  </div>
);

const trendMeta = {
  up: { icon: 'trending_up', className: 'text-positive' },
  down: { icon: 'trending_down', className: 'text-critical' },
  stable: { icon: 'trending_flat', className: 'text-muted-500' },
};

const Trend = ({ direction, label }) => {
  const meta = trendMeta[direction] || trendMeta.stable;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${meta.className}`}>
      <span className="material-icons text-sm">{meta.icon}</span>
      {label}
    </span>
  );
};

/** Brand accents in display order; tiles in a row take consecutive entries. */
export const ACCENTS = [
  { accent: 'from-brand-500 to-accent-cyan', iconClass: 'text-brand-300' },
  { accent: 'from-accent-pink to-accent-amber', iconClass: 'text-accent-pink' },
  { accent: 'from-accent-cyan to-brand-500', iconClass: 'text-accent-cyan' },
  { accent: 'from-accent-lime to-accent-cyan', iconClass: 'text-accent-lime' },
  { accent: 'from-accent-amber to-accent-pink', iconClass: 'text-accent-amber' },
  { accent: 'from-accent-cyan to-accent-lime', iconClass: 'text-accent-cyan' },
];

export const accentAt = (index) => ACCENTS[index % ACCENTS.length];

/**
 * Surface with an `accent` gradient (`from-… to-…`) that tints only the top edge
 * and a faint corner glow. The edge is the gradient peeking out above a surface
 * shifted 1px down, so it follows the rounded corners and tapers off along the
 * sides like a radiused border-top. `radius` must be a single rounded-* class
 * because it is applied to both layers. Meant for tiles, not small chips: when the
 * radius is close to half the height, the edge wraps the whole side like a bracket.
 */
export const AccentSurface = ({
  as: Tag = 'div',
  accent,
  active = false,
  accentOnHover = false,
  quietCap = false,
  radius = 'rounded-3xl',
  glowClass = '-right-12 -top-12 h-36 w-36 blur-3xl',
  className = '',
  contentClassName = '',
  children,
  ...props
}) => (
  <Tag
    className={`group relative overflow-hidden text-left ${radius} transition duration-300 ease-brand hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
      active ? 'ring-1 ring-white/10' : ''
    } ${accent ? '' : `${surface.section} ${surface.hover}`} ${className}`}
    {...props}
  >
    {accent && (
      <>
        <span
          className={`absolute inset-x-0 top-0 h-8 bg-gradient-to-r ${accent} transition-opacity duration-300 ${
            quietCap
              ? 'opacity-35 group-hover:opacity-100 group-focus-visible:opacity-100'
              : active
                ? 'opacity-100'
                : 'opacity-70 group-hover:opacity-100'
          }`}
          aria-hidden
        />
        <span className={`absolute inset-x-0 bottom-0 top-px ${radius} ${surface.section}`} aria-hidden />
        <span
          className={`pointer-events-none absolute rounded-full bg-gradient-to-br ${accent} ${glowClass} transition-all duration-500 ease-brand ${
            accentOnHover
              ? 'opacity-0 group-hover:scale-150 group-hover:opacity-[0.2] group-focus-visible:scale-150 group-focus-visible:opacity-[0.2]'
              : active
                ? 'scale-150 opacity-[0.2]'
                : 'opacity-[0.09] group-hover:scale-150 group-hover:opacity-[0.2]'
          }`}
          aria-hidden
        />
      </>
    )}
    <div className={`relative h-full ${contentClassName}`}>{children}</div>
  </Tag>
);

const statTileSizes = {
  md: { content: 'min-h-[140px] p-5', value: 'text-3xl', footer: 'pt-5' },
  sm: { content: 'p-4', value: 'text-2xl', footer: 'pt-2' },
};

/** Extra props (`as`, `onClick`, `active`, aria-*) go to `AccentSurface`. */
export const StatTile = ({
  eyebrow,
  icon,
  iconClass = 'text-brand-200',
  value,
  trend,
  sublabel,
  meta,
  size = 'md',
  as = 'article',
  ...surfaceProps
}) => {
  const s = statTileSizes[size] || statTileSizes.md;
  return (
    <AccentSurface
      as={as}
      type={as === 'button' ? 'button' : undefined}
      className="h-full w-full"
      contentClassName={`flex flex-col ${s.content}`}
      {...surfaceProps}
    >
      <div className="flex items-start justify-between gap-3">
        <Eyebrow>{eyebrow}</Eyebrow>
        {icon && (
          <span className={`material-icons text-[20px] transition-transform duration-300 ease-brand group-hover:scale-110 ${iconClass}`}>
            {icon}
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <div className={`font-display leading-none tabular-nums text-white ${s.value}`}>{value}</div>
        {trend && <Trend {...trend} />}
      </div>
      {(sublabel || meta) && (
        <div className={`mt-auto flex items-end justify-between gap-3 text-xs ${s.footer}`}>
          <span className="text-muted-400">{sublabel}</span>
          {meta && <span className="shrink-0 text-right text-muted-500">{meta}</span>}
        </div>
      )}
    </AccentSurface>
  );
};

export const StatTileSkeleton = () => (
  <div className={`flex min-h-[140px] flex-col gap-4 rounded-3xl ${surface.section} p-5`}>
    <div className="h-3 w-24 animate-pulse rounded-full bg-white/[0.05]" />
    <div className="h-8 w-32 animate-pulse rounded-full bg-white/[0.05]" />
    <div className="mt-auto h-3 w-40 animate-pulse rounded-full bg-white/[0.05]" />
  </div>
);
